import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import { createFilterOptions } from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import NewPhoneNumberComponent from "../../../common/components/new-phone-input-component/new-phone-input-component";
import { Close } from "@mui/icons-material";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../utils/extractCityStateZipCode";
import { billingDetailsValidationSchema } from "../billingDetailsValidationSchema";
import { CountryOption } from "../../../common/components/new-phone-input-component/new-phone-input-component.types";
import AmericaFlag from "../../../assets/icons/US-FLAG1.svg";
import { normalizePhoneNumber } from "../../../utils/utils";
import "./BillingDetails.scss";

interface BillingDetailsProps {
  onFormValidChange: (isValid: boolean) => void;
  onAddBillingDetails: (formValues: any) => void;
  onFormDataChange: (data: any) => void;
  onClose: () => void;
  initialValues?: any;
}

type Option = { value: string; label: string };

const BillingDetails: React.FC<BillingDetailsProps> = ({
  onFormValidChange,
  onAddBillingDetails,
  onFormDataChange,
  onClose,
  initialValues,
}) => {
  const [countryCode, setCountryCode] = useState<string>("+1");

  const countries: CountryOption[] = [
    { code: "+1", flag: AmericaFlag, label: "US" },
  ];

  const allAddressOptions = extractAllOptions(addressData);

  const defaultFilter = createFilterOptions<Option>({
    matchFrom: "any",
    stringify: (o) => `${o.label} ${o.value}`,
  });

  const limitFilterOptions = (
    options: Option[],
    state: FilterOptionsState<Option>
  ) => {
    const hasQuery = (state.inputValue || "").trim().length > 0;
    const filtered = hasQuery ? defaultFilter(options, state) : options;
    return filtered.slice(0, hasQuery ? 50 : 10);
  };

  const formik = useFormik({
    initialValues: initialValues
      ? {
        first_name: initialValues.first_name || "",
        last_name: initialValues.last_name || "",
        phoneNumber: normalizePhoneNumber(initialValues.phoneNumber) || "",
        address: initialValues.address || initialValues.street || "",
        city: initialValues.city || "",
        state: initialValues.state || "",
        country: initialValues.country || "",
        pincode: initialValues.pincode || initialValues.zipcode || "",
      }
      : {
        first_name: "",
        last_name: "",
        phoneNumber: "",
        address: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
      },
    enableReinitialize: true,
    validationSchema: billingDetailsValidationSchema,
    onSubmit: (values) => {
      console.log("Billing Info:", values);
    },
  });

  // const isFormValid = formik.isValid && Object.keys(formik.touched).length > 0;
  const isFormValid = formik.isValid && formik.dirty;

  const shouldDisableCity = useMemo(() => {
    const { state, pincode } = formik.values;
    return !state?.trim() && !pincode?.trim();
  }, [formik.values.state, formik.values.pincode]);


  useEffect(() => {
    onFormValidChange(isFormValid);
  }, [isFormValid, onFormValidChange]);

  const cityOptions = useMemo<Option[]>(() => {
    const state = formik.values.state;
    const zipcode = formik.values.pincode;

    if (state && zipcode) {
      return filterOptionsByValue(
        addressData,
        "State",
        state,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(addressData, "State", state).cityOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipcode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipcode
      ).cityOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.cityOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.pincode, formik.values.state]);

  const zipOptions = useMemo<Option[]>(() => {
    const city = formik.values.city;
    const state = formik.values.state;

    if (state && city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (state) {
      return filterOptionsByValue(
        addressData,
        "State",
        state
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(
        addressData,
        "City",
        city
      ).postCodeOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.postCodeOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.city, formik.values.state]);

  const stateOptions = React.useMemo<Option[]>(() => {
    const city = formik.values.city;
    const zipCode = formik.values.pincode;

    if (city && zipCode) {
      return filterOptionsByValue(
        addressData,
        "City",
        city,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (city) {
      return filterOptionsByValue(addressData, "City", city).stateOptions.sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    }
    if (zipCode) {
      return filterOptionsByValue(
        addressData,
        "Code",
        zipCode
      ).stateOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    return allAddressOptions.stateOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [formik.values.city, formik.values.pincode]);

  const cityByValue = useMemo(
    () => new Map(cityOptions.map((o) => [o.value, o])),
    [cityOptions]
  );

  const zipByValue = useMemo(
    () => new Map(zipOptions.map((o) => [o.value, o])),
    [zipOptions]
  );

  const stateByValue = React.useMemo(
    () => new Map(stateOptions.map((o) => [o.value, o])),
    [stateOptions]
  );

  const handleCityChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { stateOptions, postCodeOptions } = filterOptionsByValue(
        addressData,
        "City",
        newValue.value
      );

      await formik.setFieldValue("city", newValue.value, false);
      if (stateOptions.length === 1) {
        formik.setFieldValue("state", stateOptions[0].value, true);
      } else {
        formik.setFieldValue("state", "", true);
      }
      if (postCodeOptions.length === 1) {
        await formik.setFieldValue("pincode", postCodeOptions[0].value, true);
        formik.setFieldTouched("pincode", true);
      } else {
        await formik.setFieldValue("pincode", "", false);
        formik.setFieldTouched("pincode", true);
      }
    } else {
      await formik.setFieldValue("city", "", false);
      await formik.setFieldValue("pincode", "", false);
      formik.setFieldTouched("city", true);
      formik.setFieldTouched("pincode", true);
    }
    formik.setFieldTouched("city", true, true);
    await formik.validateForm();
  };

  const handleZipcodeChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { cityOptions, stateOptions } = filterOptionsByValue(
        addressData,
        "Code",
        newValue.value
      );

      await formik.setFieldValue("pincode", newValue.value, false);
      formik.setFieldTouched("pincode", true);

      if (cityOptions.length === 1) {
        await formik.setFieldValue("city", cityOptions[0].value, false);
      } else {
        formik.setFieldValue("city", "", true);
      }
      if (stateOptions.length === 1) {
        await formik.setFieldValue("state", stateOptions[0].value, false);
      } else {
        formik.setFieldValue("state", "", true);
        formik.setFieldTouched("city", true);
      }
    } else {
      await formik.setFieldValue("pincode", "", false);
      formik.setFieldTouched("pincode", true);
    }
    formik.setFieldTouched("pincode", true, true);
    await formik.validateForm();
  };

  const handleStateChange = async (_: any, newValue: Option | null) => {
    if (newValue) {
      const { postCodeOptions } = filterOptionsByValue(
        addressData,
        "State",
        newValue.value
      );
      formik.setFieldValue("state", newValue.value, true);
      if (postCodeOptions.length === 1) {
        formik.setFieldValue("pincode", postCodeOptions[0].value, true);
      } else {
        formik.setFieldValue("pincode", "", true);
      }
    } else {
      await formik.setFieldValue("state", "", false);
      await formik.setFieldValue("city", "", false);
      await formik.setFieldValue("pincode", "", false);
      formik.setFieldValue("state", "", true);
      formik.setFieldTouched("pincode", true);
      formik.setFieldTouched("city", true);
    }
    formik.setFieldTouched("state", true, true);
    await formik.validateForm();
  };

 const handleButtonClick = async () => {
  const errors = await formik.validateForm();

  formik.setTouched({
    first_name: true,
    last_name: true,
    phoneNumber: true,
    address: true,
    city: true,
    state: true,
    country: true,
    pincode: true,
  });

  if (Object.keys(errors).length === 0) {
    onFormDataChange(formik.values);
    onAddBillingDetails?.(formik.values);
    onClose();
  }
};

  const renderLabel = (label: string) => (
    <Typography variant="body2" className="billing-details__label">
      {label} <span className="billing-details__required">*</span>
    </Typography>
  );

  return (
    <Box className="billing-details">
      <Box className="billing-details__form-header">
        <Typography variant="h6" className="billing-details__title">
          Add Billing Details
        </Typography>
        <Close
          fontSize="large"
          className="billing-details__close-icon"
          onClick={onClose}
        />
      </Box>
      <Box className="billing-details__form-wrapper">
        <form onSubmit={formik.handleSubmit} className="billing-details__form">
          <Grid container spacing={4}>
            {/* First Name */}
            <Grid item xs={12} sm={6}>
              {renderLabel("First Name")}
              <TextField
                fullWidth
                placeholder="Enter First Name"
                name="first_name"
                className="billing-details__field"
                value={formik.values.first_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.first_name && Boolean(formik.errors.first_name)
                }
                helperText={
                  formik.touched.first_name && typeof formik.errors.first_name === "string"
                    ? formik.errors.first_name
                    : undefined
                }
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              {renderLabel("Last Name")}
              <TextField
                fullWidth
                placeholder="Enter Last Name"
                name="last_name"
                className="billing-details__field"
                value={formik.values.last_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.last_name && Boolean(formik.errors.last_name)
                }
                 helperText={
                  formik.touched.last_name && typeof formik.errors.last_name === "string"
                    ? formik.errors.last_name
                    : undefined
                }
              />
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} sm={6}>
              {renderLabel("Phone Number")}
              <Box
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px !important",
                    height: "36px !important",
                    bgcolor: "#fff",
                    border: "0px solid #e6e6e6",
                    width: "100%",
                  },
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                    height: "36px !important",
                    bgcolor: "#fff",
                    padding: "10px 6px !important",
                  },
                  "& .MuiInputAdornment-root": {
                    bgcolor: "#fff",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "6px 0 0 6px",
                  },
                  "& input": {
                    color: "#104145 !important",
                  },
                }}
              >
                <NewPhoneNumberComponent
                  value={formik.values.phoneNumber}
                  onChange={(value) =>
                    formik.setFieldValue("phoneNumber", value)
                  }
                  countryCode={countryCode}
                  showHelper={true}
                  onCountryChange={setCountryCode}
                  placeholder="Enter Phone Number"
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  onBlur={formik.handleBlur}
                  countries={countries}
                  helperText={
                    formik.touched.phoneNumber &&
                    typeof formik.errors.phoneNumber === "string"
                      ? formik.errors.phoneNumber
                      : undefined
                  }
                />
              </Box>
            </Grid>

            {/* Street/Area */}
            <Grid item xs={12} sm={6}>
              {renderLabel("Street/Area")}
              <TextField
                fullWidth
                placeholder="Enter Building no, Building Name, Street, Area"
                name="address"
                className="billing-details__field"
                value={formik.values.address}
                onChange={(e) => {
                  const val = e.target.value;
                  formik.setFieldValue("address", val.slice(0, 200));
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                inputProps={{ maxLength: 200 }}
                helperText={
                  formik.touched.address && typeof formik.errors.address === "string"
                    ? formik.errors.address
                    : undefined
                }
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              {renderLabel("City")}
              <Autocomplete
                options={cityOptions}
                getOptionLabel={(option) => option.label}
                value={cityByValue.get(formik.values.city) || null}
                onChange={handleCityChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                // disabled={shouldDisableCity}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select City"
                    className="billing-details__field"
                    name="city"
                    error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={
                    formik.touched.city && typeof formik.errors.city === "string"
                      ? formik.errors.city
                      : undefined
                  }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} sm={6}>
              {renderLabel("State")}
              <Autocomplete
                options={stateOptions}
                getOptionLabel={(option) => option.label}
                value={stateByValue.get(formik.values.state) || null}
                onChange={handleStateChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select State"
                    className="billing-details__field"
                    name="state"
                    error={formik.touched.state && Boolean(formik.errors.state)}
                 helperText={
                    formik.touched.state && typeof formik.errors.state === "string"
                      ? formik.errors.state
                      : undefined
                  }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={6}>
              {renderLabel("Country")}
              <TextField
                select
                fullWidth
                name="country"
                className="billing-details__field"
                value={formik.values.country}
                onChange={(e) =>
                  formik.setFieldValue("country", e.target.value)
                }
                helperText={
                  formik.touched.country && typeof formik.errors.country === "string"
                    ? formik.errors.country
                    : undefined
                }
                onBlur={formik.handleBlur}
                error={formik.touched.country && Boolean(formik.errors.country)}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9e9e9e" }}>
                          -- Select Country --
                        </span>
                      );
                    }
                    return selected;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  -- Select Country --
                </MenuItem>
                <MenuItem value="USA">USA</MenuItem>
              </TextField>
            </Grid>

            {/* Zipcode */}
            <Grid item xs={12} sm={6}>
              {renderLabel("Zipcode")}
              <Autocomplete
                options={zipOptions}
                getOptionLabel={(option) => option.label}
                value={zipByValue.get(formik.values.pincode) || null}
                onChange={handleZipcodeChange}
                onBlur={formik.handleBlur}
                filterOptions={limitFilterOptions}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Zipcode"
                    className="billing-details__field"
                    name="pincode"
                    error={
                      formik.touched.pincode && Boolean(formik.errors.pincode)
                    }
                    helperText={
                      formik.touched.pincode && typeof formik.errors.pincode === "string"
                        ? formik.errors.pincode
                        : undefined
                    }
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Grid>
          </Grid>
        </form>
      </Box>

      <Box className="billing-details__button-container">
        <Button
          type="button"
          variant="outlined"
          className="billing-details__button-cancel"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          className={`billing-details__button ${isFormValid
            ? "billing-details__button--enabled"
            : "billing-details__button--disabled"
            }`}
          onClick={handleButtonClick}
          // disabled={!isFormValid}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default BillingDetails;
